import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { overlayStack } from '../../lib/overlayStack';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  /** Optional persistent column rendered alongside children in a 2-column layout */
  sidebar?: React.ReactNode;
  /** Position of the sidebar column. Default: 'left' */
  sidebarPosition?: 'left' | 'right';
  footer?: React.ReactNode;
  headerActions?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  noPadding?: boolean;
  zIndexClass?: string;
}

export function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  headerActions,
  sidebar,
  sidebarPosition = 'left',
  footer,
  maxWidth = 'md',
  noPadding = false,
  zIndexClass = 'z-[70]',
}: DrawerProps) {
  // Register with overlay stack for ESC and body scroll lock
  const overlayIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      overlayIdRef.current = overlayStack.push(onClose);
    } else if (overlayIdRef.current) {
      overlayStack.remove(overlayIdRef.current);
      overlayIdRef.current = null;
    }
    return () => {
      if (overlayIdRef.current) {
        overlayStack.remove(overlayIdRef.current);
        overlayIdRef.current = null;
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isBottomMost = overlayIdRef.current ? overlayStack.isBottomMost(overlayIdRef.current) : true;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  }[maxWidth];

  return (
    <div 
      className={`fixed inset-0 ${zIndexClass} overflow-hidden`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-title"
    >
      {/* Backdrop — rendered softly only if it's the bottom-most overlay or with reduced opacity */}
      <div
        className={`fixed inset-0 transition-opacity duration-300 animate-fade-in ${
          isBottomMost ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/20'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Container */}
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-6 sm:pl-10">
        <div
          className={`w-screen ${maxWidthClasses} bg-surface border-l border-border shadow-2xl flex flex-col transform transition-transform duration-300 ease-out animate-slide-in`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3.5 py-3 sm:px-6 sm:py-4 border-b border-border bg-surface-secondary/40 shrink-0">
            <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
              {Icon && (
                <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange shrink-0">
                  <Icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
                </div>
              )}
              <div className="min-w-0">
                <h2 id="drawer-title" className="text-base sm:text-lg font-bold text-text-primary truncate">{title}</h2>
                {subtitle && (
                  <p className="text-xs text-text-secondary mt-0.5 hidden sm:block truncate">{subtitle}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {headerActions}
              <button
                onClick={onClose}
                className="btn-press rounded-xl p-2 sm:p-2.5 min-h-[40px] min-w-[40px] sm:min-h-[44px] sm:min-w-[44px] flex items-center justify-center text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary transition-colors cursor-pointer shrink-0"
                title="Cerrar panel"
                aria-label="Cerrar panel"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body Content Area — Clean Single Scroll per Column without space-y-4 layout bug */}
          <div 
            className={`flex-1 flex min-h-0 ${
              noPadding 
                ? 'overflow-hidden' 
                : sidebar 
                  ? 'overflow-hidden p-3.5 sm:p-6 gap-3.5 sm:gap-6 flex-col lg:flex-row' 
                  : 'overflow-y-auto custom-scrollbar p-3.5 sm:p-6 flex-col gap-3.5 sm:gap-4'
            }`}
          >
            {sidebar && sidebarPosition === 'left' && (
              <aside className="hidden lg:flex w-full lg:w-72 shrink-0 flex-col gap-4 lg:border-r lg:border-border lg:pr-6 overflow-y-auto custom-scrollbar">
                {sidebar}
              </aside>
            )}

            <div 
              className={`flex-1 min-w-0 flex flex-col gap-4 ${
                sidebar ? 'overflow-y-auto custom-scrollbar' : ''
              }`}
            >
              {children}
            </div>

            {sidebar && sidebarPosition === 'right' && (
              <aside className="hidden lg:flex w-full lg:w-80 shrink-0 flex-col gap-4 lg:border-l lg:border-border lg:pl-6 overflow-y-auto custom-scrollbar">
                {sidebar}
              </aside>
            )}
          </div>

          {/* Sticky Footer */}
          {footer && (
            <div className="px-6 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] border-t border-border bg-surface-secondary/80 flex items-center justify-end gap-3 shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
